// src/api/tariffApi.ts
import axios from 'axios';
import { TariffItem } from '../types/tariff';

// Use the public URL of your Render backend here
// Make sure to replace this with your actual Render URL
const API_BASE_URL = 'https://imports-lk.onrender.com';
// const API_BASE_URL = 'http://localhost:8000';

export interface CalculateTaxParams {
  hs_code: string;
  cif_value: number;
  currency: string;
  duty_type: "GEN" | "PREF";
  quantity?: number;
  sugar_quantity?: number;
  mpr_quantity?: number;
  cc_quantity?: number;
  cm3_quantity?: number;
}

export interface CalculateTaxResponse {
  customs_duty: number;
  duty_type?: string;
  general_duty_calculation_details?: string;
  cess: number;
  cess_sg: number;
  cess_calculation_details?: string;
  cess_raw_rate?: string;
  pal: number;
  pal_sg: number;
  surcharge: number;
  excise: number;
  excise_calculation_details?: string;
  excise_raw_rate?: string;
  vat: number;
  sscl: number;
  scl: number;
  luxury_tax: number;
  luxury_tax_calculation_details?: string;
  luxury_tax_raw_rate?: string;
  total_levies: number;
  total_with_cif: number;
  cif_value_lkr: number;
  unit_candidates_detected?: any[];
}

// **ENSURE 'export' IS HERE**
export interface SearchApiResponse {
  query: string;
  category: string;
  results: TariffItem[];
}

// **ENSURE 'export' IS HERE**
export const searchTariffData = async (
  query: string,
  category: string | null,
  isExact: boolean = false // New parameter with default false (fuzzy)
): Promise<SearchApiResponse> => {
  try {
    const params = new URLSearchParams();
    if (query) params.append('q', query);
    if (category) params.append('category', category);
    if (isExact) params.append('exact', 'true'); // Pass the exact parameter if true

    const response = await axios.get<SearchApiResponse>(`${API_BASE_URL}/search/`, { params });
    return response.data;
  } catch (error) {
    console.error("Error searching tariffs:", error);
    throw error;
  }
};

// **ENSURE 'export' IS HERE**
export const getTaxDetails = async (hsCode: string): Promise<TariffItem> => {
  try {
    const response = await axios.get<TariffItem>(`${API_BASE_URL}/tax/${encodeURIComponent(hsCode)}`);
    return response.data;
  } catch (error) {
    console.error(`Failed to fetch tax details for HS Code ${hsCode}:`, error);
    throw error;
  }
};

// **ENSURE 'export' IS HERE**
export const getCategories = async (): Promise<string[]> => {
  try {
    const response = await axios.get<string[]>(`${API_BASE_URL}/categories`);
    return response.data;
  } catch (error) {
    console.error("Failed to fetch categories:", error);
    throw error;
  }
};

// **ENSURE 'export' IS HERE**
export const downloadPdf = async (hsCode: string): Promise<void> => {
  try {
    const response = await axios.get(`${API_BASE_URL}/pdf/${encodeURIComponent(hsCode)}`, {
      responseType: 'blob', // Important for downloading files
    });

    // Create a blob URL and trigger download
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Tariff_Details_${hsCode}.pdf`); // Set the filename
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url); // Clean up the URL
    alert(`PDF for HS Code ${hsCode} downloaded successfully!`);
  } catch (error) {
    console.error(`Failed to download PDF for HS Code ${hsCode}:`, error);
    alert(`Failed to download PDF for HS Code ${hsCode}. Please try again.`);
    throw error; // Re-throw to be caught by the modal
  }

};
export const sendWakeUpRequest = async (): Promise<void> => {
  try {
    await fetch(`${API_BASE_URL}/wake-up`);
    console.log('Wake-up request sent successfully.');
  } catch (error) {
    console.error('Failed to send wake-up request:', error);
  }
};

// **ENSURE 'export' IS HERE**
export const getExchangeRates = async (): Promise<Record<string, number>> => {
  try {
    const response = await axios.get<{ exchange_rates: Record<string, number> }>(`${API_BASE_URL}/config`);
    return response.data.exchange_rates;
  } catch (error) {
    console.error("Failed to fetch exchange rates:", error);
    // Fallback to defaults if fetch fails to prevent crash
    return {
      "LKR": 1.0,
      "USD": 300.0,
      "EUR": 335.0,
      "AUD": 200.0
    };
  }
};

export const calculateTax = async (params: CalculateTaxParams): Promise<CalculateTaxResponse> => {
  try {
    const queryParams = new URLSearchParams({
      hs_code: params.hs_code,
      cif_value: params.cif_value.toString(),
      currency: params.currency,
      duty_type: params.duty_type,
      quantity: (params.quantity || 0).toString(),
      sugar_quantity: (params.sugar_quantity || 0).toString(),
      mpr_quantity: (params.mpr_quantity || 0).toString(),
      cc_quantity: (params.cc_quantity || 0).toString(),
      cm3_quantity: (params.cm3_quantity || 0).toString(),
    });

    const response = await axios.get<CalculateTaxResponse>(
      `${API_BASE_URL}/calculate_tax/${encodeURIComponent(params.hs_code)}`,
      { params: queryParams }
    );

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(error.response.data.detail || "Calculation failed");
    }
    throw error;
  }
};
